import { useCallback, useEffect, useRef } from 'react'

const OVERLAY_STATE_KEY = '__casinoGameOverlay'

/**
 * Mobile browser "back" navigates the global history stack. Casino games open in an
 * overlay (no route change), so the first back would leave the page (home/login).
 * Push a dummy history entry while the overlay is open so the first back only closes
 * the game and stays on the current route.
 */
export function useGameOverlayHistory(isOpen: boolean, onClose: () => void) {
  const pushedRef = useRef(false)
  const ignorePopRef = useRef(false)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  const closeOverlay = useCallback(() => {
    if (pushedRef.current) {
      ignorePopRef.current = true
      window.history.go(-1)
      pushedRef.current = false
    }
    onCloseRef.current()
  }, [])

  useEffect(() => {
    if (!isOpen) return

    if (!pushedRef.current) {
      window.history.pushState({ [OVERLAY_STATE_KEY]: true }, '', window.location.href)
      pushedRef.current = true
    }

    const onPop = () => {
      if (ignorePopRef.current) {
        ignorePopRef.current = false
        return
      }
      if (pushedRef.current) {
        pushedRef.current = false
        onCloseRef.current()
      }
    }

    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [isOpen])

  // Overlay closed without a user back navigation (load error, etc.) — drop dummy entry
  useEffect(() => {
    if (isOpen) return
    if (!pushedRef.current) return
    pushedRef.current = false
    window.history.go(-1)
  }, [isOpen])

  // If user navigates away while overlay is open, remove dummy history entry
  useEffect(() => {
    return () => {
      if (!pushedRef.current) return
      pushedRef.current = false
      window.history.go(-1)
    }
  }, [])

  return closeOverlay
}
